import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { causalApi, type CausalScope } from '../api/CausalApi'
import { causalNotificationApi } from '../api/CausalNotificationApi'
import type {
  ActiveExecution,
  AnalysisRun,
  AnalysisSchedule,
  CausalProfile,
  CausalProgressEvent,
  ImpactPageState,
  MonthlyQuota,
} from '../models/impact.types'
import { runsForSchedule } from '../service/analysisListMapper'

import type { ChannelAvailability } from '@/features/workspace-settings'
import { ApiError } from '@/shared/http'

const EMPTY_ACTIVE = (): ActiveExecution => ({
  executionId: null,
  scheduleId: null,
  name: null,
  status: 'idle',
  phase: null,
})

function canceled(error: unknown): boolean {
  return ApiError.isApiError(error) && error.kind === 'canceled'
}

function apiError(error: unknown): ApiError {
  return ApiError.isApiError(error) ? error : ApiError.fromAxios(error)
}

export const useImpactStore = defineStore('impactStore', () => {
  const profiles = ref<CausalProfile[]>([])
  const schedules = ref<AnalysisSchedule[]>([])
  const runs = ref<AnalysisRun[]>([])
  const quota = ref<MonthlyQuota | null>(null)
  const channels = ref<ChannelAvailability[]>([])
  const notificationTypeId = ref<number | null>(null)
  const active = ref<ActiveExecution>(EMPTY_ACTIVE())
  const overlayExecutionId = ref<string | null>(null)
  const status = ref<ImpactPageState>('idle')
  const error = ref<ApiError | null>(null)
  const hasLoadedSchedules = ref(false)
  const hasLoadedRuns = ref(false)

  let currentScope: CausalScope | null = null
  let generation = 0

  const isEmpty = computed(() => status.value === 'ready' && schedules.value.length === 0)
  const hasQuota = computed(() => quota.value?.isHasQuota ?? true)
  const activeIsRunning = computed(
    () => active.value.status === 'pending' || active.value.status === 'running',
  )

  function resetData(): void {
    profiles.value = []
    schedules.value = []
    runs.value = []
    quota.value = null
    channels.value = []
    notificationTypeId.value = null
    active.value = EMPTY_ACTIVE()
    overlayExecutionId.value = null
    error.value = null
    hasLoadedSchedules.value = false
    hasLoadedRuns.value = false
  }

  function reset(): void {
    generation += 1
    currentScope = null
    resetData()
    status.value = 'idle'
  }

  function seedActiveFromRuns(): void {
    const running = runs.value.find((run) => run.status === 'running' || run.status === 'pending')
    if (!running) return
    active.value = {
      executionId: running.execution_id,
      scheduleId: running.schedule_id,
      name: running.name ?? null,
      status: running.status,
      phase: running.phase ?? null,
    }
  }

  /**
   * `loadChannels` is injected rather than called here: the channel read belongs to module 22
   * (ledger §B2, reversed 2026-08-06), it is reached through `useNotificationChannels().loadFor`,
   * and a store may not call another feature's composable. The page supplies it — the same shape
   * module 17's `alertsStore.load` uses.
   */
  async function loadBootstrap(
    scope: CausalScope,
    signal: AbortSignal | undefined,
    loadChannels: () => Promise<ChannelAvailability[]>,
  ): Promise<void> {
    const requestGeneration = ++generation
    currentScope = scope
    resetData()
    status.value = 'loading'

    const [profilesResult, schedulesResult, runsResult, quotaResult, channelsResult, typeResult] =
      await Promise.allSettled([
        causalApi.getProfiles(scope, signal),
        causalApi.getSchedules(scope, signal),
        causalApi.getRuns(scope, signal),
        causalApi.getQuota(scope, signal),
        loadChannels(),
        causalNotificationApi.resolveNotificationTypeId(),
      ])

    if (requestGeneration !== generation || signal?.aborted) return

    profiles.value = profilesResult.status === 'fulfilled' ? profilesResult.value : []
    schedules.value = schedulesResult.status === 'fulfilled' ? schedulesResult.value : []
    runs.value = runsResult.status === 'fulfilled' ? runsResult.value : []
    quota.value = quotaResult.status === 'fulfilled' ? quotaResult.value : null
    channels.value = channelsResult.status === 'fulfilled' ? channelsResult.value : []
    notificationTypeId.value = typeResult.status === 'fulfilled' ? typeResult.value : null
    hasLoadedSchedules.value = schedulesResult.status === 'fulfilled'
    hasLoadedRuns.value = runsResult.status === 'fulfilled'

    const coreFailures = [schedulesResult, runsResult].filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    )
    if (coreFailures.length === 2 && !coreFailures.every((result) => canceled(result.reason))) {
      error.value = apiError(coreFailures[0]?.reason)
      status.value = 'error'
      return
    }

    seedActiveFromRuns()
    status.value = 'ready'
  }

  async function refreshRunsAndSchedules(options: { silent?: boolean } = {}): Promise<void> {
    const scope = currentScope
    if (!scope) return
    if (!options.silent) status.value = 'loading'
    const requestGeneration = generation
    const [runsResult, schedulesResult] = await Promise.allSettled([
      causalApi.getRuns(scope),
      causalApi.getSchedules(scope),
    ])
    if (requestGeneration !== generation) return
    if (runsResult.status === 'fulfilled') {
      runs.value = runsResult.value
      hasLoadedRuns.value = true
    }
    if (schedulesResult.status === 'fulfilled') {
      schedules.value = schedulesResult.value
      hasLoadedSchedules.value = true
    }
    if (!options.silent) status.value = 'ready'
  }

  async function refreshQuota(): Promise<void> {
    const scope = currentScope
    if (!scope) return
    try {
      quota.value = await causalApi.getQuota(scope)
    } catch (cause) {
      if (!canceled(cause)) quota.value = null
    }
  }

  function applyProgressEvent(event: CausalProgressEvent): void {
    active.value = {
      executionId: event.executionId,
      scheduleId: event.scheduleId,
      name: event.name,
      status: event.status,
      phase: event.phase,
    }

    const index = runs.value.findIndex((run) => run.execution_id === event.executionId)
    if (index >= 0) {
      const run = runs.value[index]
      if (run) {
        runs.value[index] = {
          ...run,
          status: event.status,
          phase: event.phase,
          ...(event.name ? { name: event.name } : {}),
        }
      }
      return
    }
    if (event.scheduleId !== null) {
      runs.value.unshift({
        execution_id: event.executionId,
        schedule_id: event.scheduleId,
        status: event.status,
        phase: event.phase,
        created_at: '',
        ...(event.name ? { name: event.name } : {}),
      })
    }
  }

  function trackExecution(run: AnalysisRun): void {
    active.value = {
      executionId: run.execution_id,
      scheduleId: run.schedule_id,
      name: run.name ?? null,
      status: run.status,
      phase: run.phase ?? null,
    }
    overlayExecutionId.value = run.execution_id
  }

  function dismissOverlay(): void {
    overlayExecutionId.value = null
  }

  function runsBySchedule(scheduleId: number): AnalysisRun[] {
    return runsForSchedule(runs.value, scheduleId)
  }

  function profileBySlug(slug: string): CausalProfile | null {
    return profiles.value.find((profile) => profile.slug === slug) ?? null
  }

  function removeSchedule(scheduleId: number): void {
    schedules.value = schedules.value.filter(({ id }) => id !== scheduleId)
    runs.value = runs.value.filter((run) => run.schedule_id !== scheduleId)
    if (active.value.scheduleId === scheduleId) active.value = EMPTY_ACTIVE()
  }

  function patchSchedule(schedule: AnalysisSchedule): void {
    const index = schedules.value.findIndex(({ id }) => id === schedule.id)
    if (index < 0) {
      schedules.value.unshift(schedule)
      return
    }
    schedules.value[index] = schedule
  }

  return {
    profiles,
    schedules,
    runs,
    quota,
    channels,
    notificationTypeId,
    active,
    overlayExecutionId,
    status,
    error,
    hasLoadedSchedules,
    hasLoadedRuns,
    isEmpty,
    hasQuota,
    activeIsRunning,
    loadBootstrap,
    refreshRunsAndSchedules,
    refreshQuota,
    applyProgressEvent,
    trackExecution,
    dismissOverlay,
    runsBySchedule,
    profileBySlug,
    removeSchedule,
    patchSchedule,
    reset,
  }
})
